import * as React from "react";
import Box from "../../components/box";
import { CartMain as Container } from "./main";
import { Paths } from "shared/paths";
import { useLocation } from "wouter";
import { useState } from "client/hooks";
import { Cart, Dev } from "../../stores";
import { Link } from "wouter";
import { MdArrowRight as RightArrow } from "react-icons/md";
import { Option } from "@nozzlegear/railway";
import { Sentry } from "client/modules/sentry";

type Props = {};

interface State {
    error: Option<string>;
    firstName: string;
    familyName: string
}

export function CartNamePage(_: Props): JSX.Element {
    const isSampleOnly = Cart.hasOnlySampleItems;
    if (isSampleOnly) {
        // Samples should skip this page, they do not (and cannot) enter a decedent name.
        // Rendering through Container lets the cart guard redirect them to /cart/shipping
        // instead of leaving them on a blank page.
        return (<Container />);
    }

    const [location, setLocation] = useLocation();
    const [state, setState] = useState<State>({
        error: Option.ofNone(),
        firstName: Cart.decedent_first_name ?? "",
        familyName: Cart.decedent_family_name ?? "",
    });

    const handleContinue = (e: React.MouseEvent<any> | React.FormEvent<any>) => {
        e.preventDefault();

        if (!state.firstName) {
            setState({ error: Option.ofSome("You must enter the first name of the decedent.") });
            return;
        }

        if (!state.familyName) {
            setState({ error: Option.ofSome("You must enter the family name of the decedent.") });
            return;
        }

        Cart.setNames(state.firstName, state.familyName);

        const purchaseOrderIds = Cart.items
            .map((li) => li.purchaseOrderId)
            .filter((id): id is string => id != null);

        Sentry.addBreadcrumb({
            message: "User completed name step, navigating to shipping",
            category: "Kmmp.Browser.Cart.Name",
            level: "info",
            data: {
                purchaseOrderIds,
                lineItemCount: Cart.items.length,
            },
        });

        setLocation(Paths.cart.shipping);
    };

    const setName = React.useCallback((type: "first" | "family") => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (type === "first") {
            setState({ firstName: e.currentTarget.value });
        } else if (type === "family") {
            setState({ familyName: e.currentTarget.value });
        }
    }, [setState]);

    const { error, firstName, familyName } = state;
    const description = `Your order is ready for checkout. Enter the name of the deceased below, and remember that all items in an order must be for the same decedent.`;
    const footer = (
        <div className="align-children justify-space-between">
            {Dev.allow_cart ? (
                <Link to={Paths.cart.index} className="btn">
                    Back
                </Link>
            ) : (
                <span />
            )}
            <button type="button" className="btn blue" onClick={(e) => handleContinue(e)}>
                {"Continue"}
                <RightArrow color={`#fff`} size={18} className={`right`} />
            </button>
        </div>
    );
    return (
        <Container>
        <Box title={"Name of Deceased"} description={description} error={error.defaultValue("")} footer={footer}>
                <div className="control-group">
                    <label>First Name of Deceased</label>
                    <input type="text"
                        autoComplete="off"
                        value={firstName}
                        placeholder="John"
                        onChange={setName("first")} />
                </div>
                <div className="control-group">
                    <label>Last Name of Deceased</label>
                    <input type="text"
                        autoComplete="off"
                        value={familyName}
                        placeholder="Smith"
                        onChange={setName("family")} />
                </div>
            </Box>
        </Container>
    );
}
